/**
 * Browser information and fingerprinting for B2M Tracker
 */
export default class BrowserInfo {
  constructor() {
    // Cache parsed UA result — browser info is static for the session lifetime
    this._autoProps = null;
  }

  /**
   * Parse navigator.userAgent into structured device/browser properties.
   * Returns the same property names as Mixpanel's auto-collected set so
   * backend breakdowns work consistently across platforms.
   * @param {string} [ua]
   * @returns {{ os, os_version, browser, browser_version, device_type }}
   */
  parseUA(ua) {
    const str = ua || navigator.userAgent || '';

    // ── Device type ──────────────────────────────────────────────────────────
    const isMobile = /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(str);
    const isTablet = /iPad|Tablet|PlayBook/i.test(str) ||
      (/Android/i.test(str) && !/Mobile/i.test(str));
    const deviceType = isTablet ? 'Tablet' : (isMobile ? 'Mobile' : 'Desktop');

    // ── OS ───────────────────────────────────────────────────────────────────
    let os = 'Unknown';
    let osVersion = '';

    if (/Windows NT/i.test(str)) {
      os = 'Windows';
      const m = str.match(/Windows NT ([\d.]+)/);
      const map = { '10.0': '10', '6.3': '8.1', '6.2': '8', '6.1': '7', '6.0': 'Vista', '5.1': 'XP' };
      osVersion = m ? (map[m[1]] || m[1]) : '';
    } else if (/iPhone|iPod/i.test(str)) {
      os = 'iOS';
      const m = str.match(/OS ([\d_]+)/);
      osVersion = m ? m[1].replace(/_/g, '.') : '';
    } else if (/iPad/i.test(str)) {
      os = 'iPadOS';
      const m = str.match(/OS ([\d_]+)/);
      osVersion = m ? m[1].replace(/_/g, '.') : '';
    } else if (/Android/i.test(str)) {
      os = 'Android';
      const m = str.match(/Android ([\d.]+)/);
      osVersion = m ? m[1] : '';
    } else if (/Macintosh|Mac OS X/i.test(str)) {
      os = 'macOS';
      const m = str.match(/Mac OS X ([\d_.]+)/);
      osVersion = m ? m[1].replace(/_/g, '.') : '';
    } else if (/CrOS/i.test(str)) {
      os = 'Chrome OS';
    } else if (/Linux/i.test(str)) {
      os = 'Linux';
    }

    // ── Browser ──────────────────────────────────────────────────────────────
    let browser = 'Unknown';
    let browserVersion = '';

    if (/SamsungBrowser\/([\d.]+)/i.test(str)) {
      browser = 'Samsung Browser';
      browserVersion = str.match(/SamsungBrowser\/([\d.]+)/i)[1];
    } else if (/OPR\/([\d.]+)/i.test(str)) {
      browser = 'Opera';
      browserVersion = str.match(/OPR\/([\d.]+)/i)[1];
    } else if (/Edg\/([\d.]+)/i.test(str)) {
      browser = 'Edge';
      browserVersion = str.match(/Edg\/([\d.]+)/i)[1];
    } else if (/UCBrowser\/([\d.]+)/i.test(str)) {
      browser = 'UC Browser';
      browserVersion = str.match(/UCBrowser\/([\d.]+)/i)[1];
    } else if (/Firefox\/([\d.]+)/i.test(str)) {
      browser = 'Firefox';
      browserVersion = str.match(/Firefox\/([\d.]+)/i)[1];
    } else if (/Chrome\/([\d.]+)/i.test(str)) {
      browser = 'Chrome';
      browserVersion = str.match(/Chrome\/([\d.]+)/i)[1];
    } else if (/Version\/([\d.]+).*Safari/i.test(str)) {
      browser = 'Safari';
      browserVersion = str.match(/Version\/([\d.]+)/i)[1];
    } else if (/Trident.*rv:([\d.]+)/i.test(str) || /MSIE ([\d.]+)/i.test(str)) {
      browser = 'Internet Explorer';
      const m = str.match(/(?:rv:|MSIE )([\d.]+)/i);
      browserVersion = m ? m[1] : '';
    }

    return {
      os,
      os_version:      osVersion,
      browser,
      browser_version: browserVersion ? browserVersion.split('.')[0] : '',
      device_type:     deviceType
    };
  }

  /**
   * Returns auto-collected device/environment properties that should be
   * attached to every event (Mixpanel parity).
   * Result is cached because these values are static for the page lifetime.
   * @returns {Object}
   */
  getAutoProperties() {
    if (this._autoProps) return this._autoProps;

    const nav    = window.navigator || {};
    const screen = window.screen    || {};
    const parsed = this.parseUA(nav.userAgent);

    this._autoProps = {
      os:              parsed.os,
      os_version:      parsed.os_version     || undefined,
      browser:         parsed.browser,
      browser_version: parsed.browser_version || undefined,
      device_type:     parsed.device_type,
      screen_width:    screen.width          || undefined,
      screen_height:   screen.height         || undefined,
      language:        nav.language          || undefined,
      timezone:        Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    };

    // Remove undefined keys so null-free event_params are sent
    Object.keys(this._autoProps).forEach(k => {
      if (this._autoProps[k] === undefined) delete this._autoProps[k];
    });

    return this._autoProps;
  }

  getInfo() {
    const screen = window.screen || {};
    const nav = window.navigator || {};
    
    return {
      user_agent: nav.userAgent || "",
      http_accept_headers: nav.languages ? nav.languages.join(',') : nav.language || "",
      plugins: this.getPluginsString(),
      timezone_offset: new Date().getTimezoneOffset().toString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      screen: screen.width + "x" + screen.height + "x" + screen.colorDepth,
      system_fonts: [],  // This would require more complex detection
      hash_of_canvas_fingerprint: this.getCanvasFingerprint(),
      hash_of_webgl_fingerprint: "",  // Would require WebGL implementation
      webgl_vendor_renderer: this.getWebGLInfo(),
      dnt_header_enabled: nav.doNotTrack === "1",
      language: nav.language || "",
      platform: nav.platform || "",
      touch_support: "ontouchstart" in window ? "supported" : "not supported",
      audio_context_fingerprint: "",  // Would require AudioContext implementation
      cpu_class: nav.cpuClass || "",
      hardware_concurrency: nav.hardwareConcurrency || 0,
      device_memory: nav.deviceMemory || 0
    };
  }
  
  getPluginsString() {
    if (!navigator.plugins) return "";
    
    const plugins = [];
    for (let i = 0; i < navigator.plugins.length; i++) {
      plugins.push(navigator.plugins[i].name);
    }
    return plugins.join(',');
  }
  
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 200;
      canvas.height = 50;
      
      ctx.textBaseline = "top";
      ctx.font = "14px 'Arial'";
      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = "#f60";
      ctx.fillRect(0, 0, 200, 50);
      ctx.fillStyle = "#069";
      ctx.fillText("B2Mtracker,canvas", 2, 15);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillText("B2Mtracker,canvas", 4, 17);
      
      const dataUrl = canvas.toDataURL();
      return this.simpleHash(dataUrl);
    } catch (e) {
      return "";
    }
  }
  
  getWebGLInfo() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return "";
      
      const vendor = gl.getParameter(gl.VENDOR);
      const renderer = gl.getParameter(gl.RENDERER);
      
      return vendor + "~" + renderer;
    } catch (e) {
      return "";
    }
  }
  
  simpleHash(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return hash.toString();
  }
} 